import { Box, Skeleton, Stack, Typography } from '@mui/material';
import {
  AnalyticsEventName,
  AssessmentAnalyticsProvider,
  trackAmplitudeEvent,
} from '@schooly/analytics';
import {
  AssessmentsGrade,
  AssessmentWithEntries,
  FilterKeys,
  GetAssessmentsEntriesSort,
  isAssessmentEntryComment,
  ReportForAssessment,
  SORT_DIRECTION,
  StudentSchoolRelation,
  useGetAssessmentsEntriesQuery,
  useGetAssessmentsGradesForSchoolQuery,
  useGetMembership,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { ArrangeByDropdown, getArrangedAssessmentsEntries } from '@schooly/components/filters';
import { PROPERTIES_TEXT_IDS } from '@schooly/constants';
import { useFlag } from '@schooly/hooks/use-flag';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import {
  CommentFilledIcon,
  Counter,
  GridRowCell,
  GridRowItem,
  GridRowName,
  GridRowStyled,
  randomInt,
  SimpleButton,
} from '@schooly/style';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ListViewAssessmentEntries } from '../../../../components/uikit-components/ListViewAssessmentEntries/ListViewAssessmentsEntries';
import { getUserFullName } from '../../../../helpers/users';
import { WithRef } from '../../../../hooks/useWithRef';
import {
  ReportsPreviewEntriesContent,
  ReportsPreviewEntriesHeader,
  ReportsPreviewEntriesStyled,
} from './ReportsPreviewEntries.styled';

const SKELETON_ROWS_COUNT = 6;

type ReportAssessmentsArrangeBy = FilterKeys.Subject | FilterKeys.AreaOfLearning;

export const REPORT_ASSESSMENTS_ARRANGE_BY_FILTER_KEYS = [
  FilterKeys.Subject,
  FilterKeys.AreaOfLearning,
] as const;

const DEFAULT_REPORT_ASSESSMENTS_SORT: GetAssessmentsEntriesSort[] = [
  { columnTextId: 'group_type', direction: SORT_DIRECTION.DESC },
  { columnTextId: 'subject_order', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'group_name', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
  { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
];

export interface ReportsPreviewEntriesProps {
  showEmptyStub: boolean;
  report?: ReportForAssessment;
  recipientId?: string;
  fullView: boolean;
}

const PAGE_SIZE = 30;

export const ReportsPreviewEntries: FC<ReportsPreviewEntriesProps> = ({
  recipientId,
  report,
  showEmptyStub,
  fullView,
}) => {
  const { schoolId = '' } = useAuth();

  const { data: grades } = useGetAssessmentsGradesForSchoolQuery(
    { schoolId },
    {
      refetchOnMount: 'always',
      enabled: !!schoolId,
    },
  );

  const lists = useMemo(() => {
    return grades?.reduce(
      (prev, list) => {
        if (list) {
          prev[list.id] = list;
        }

        return prev;
      },
      {} as Record<string, AssessmentsGrade>,
    );
  }, [grades]);

  const { data: recipient } = useGetMembership(
    'student',
    {
      schoolId,
      id: recipientId || '',
    },
    { enabled: !!recipientId },
  );

  const renderContent = () => {
    if (showEmptyStub) return null;
    if (!recipient || !lists || !report)
      return (
        <>
          <Typography variant="h2" sx={(theme) => ({ padding: theme.spacing(2.5, 0, 2.5, 0.5) })}>
            <Skeleton width={150} height={30} />
          </Typography>
          <ReportPreviewEntriesListSkeleton rowCount={SKELETON_ROWS_COUNT} />
        </>
      );

    return (
      <AssessmentAnalyticsProvider source="report" grades={grades ?? []}>
        <RecipientAssessments
          // Force recalculate memoized table data from cache
          key={recipient.relation_id}
          lists={lists}
          report={report}
          recipient={recipient}
          schoolId={schoolId}
          fullView={fullView}
        />
      </AssessmentAnalyticsProvider>
    );
  };

  return <ReportsPreviewEntriesStyled>{renderContent()}</ReportsPreviewEntriesStyled>;
};

type RecipientAssessmentsProps = {
  lists: Record<string, AssessmentsGrade>;
  report: ReportForAssessment;
  recipient: StudentSchoolRelation;
  schoolId: string;
  fullView: boolean;
};

const RecipientAssessments: FC<RecipientAssessmentsProps> = ({
  report,
  recipient,
  lists,
  schoolId,
  fullView,
}) => {
  const { formatMessage } = useIntl();

  const [commentsShown, showComments, hideComments] = useFlag(false);

  const arrangeByDropdown = useRef<ArrangeByDropdown | null>(null);
  const [arrangeBy, setArrangeBy] = useState<ReportAssessmentsArrangeBy>(
    REPORT_ASSESSMENTS_ARRANGE_BY_FILTER_KEYS[0],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    // reset showAllComments on switching between recipients
    hideComments();
  }, [recipient.relation_id, hideComments]);

  const { data, setParams, hasNextPage, isLoading, isFetchingNextPage, fetchNextPage } =
    useGetAssessmentsEntriesQuery(
      {
        schoolId,
        pageSize: PAGE_SIZE,
        sort: DEFAULT_REPORT_ASSESSMENTS_SORT,
        filters: {
          [FilterKeys.Relation]: [recipient.relation_id],
          [FilterKeys.Report]: [report.id],
        },
      },
      {
        refetchOnMount: 'always',
        enabled: Boolean(schoolId && recipient.relation_id && report.id),
      },
    );
  const total = data?.pages[0]?.count;

  const containerRef = useRef<HTMLDivElement>(null);

  const loaderRef = useInfiniteScroll(isLoading || isFetchingNextPage, fetchNextPage, hasNextPage);

  const { entries, assessments } = useMemo(() => {
    const assessments =
      data?.pages.reduce<AssessmentWithEntries[]>((prev, curr) => {
        prev.push(...curr.results);
        return prev;
      }, []) ?? [];

    return {
      entries: getArrangedAssessmentsEntries(assessments, arrangeBy),
      assessments,
    };
  }, [data?.pages, arrangeBy]);

  const hasComments = useMemo(
    () =>
      Boolean(
        assessments.some((assessment) => {
          const group = assessment.groups[0];
          if (!group) return false;
          return group.students.some(
            (student) =>
              student.relation_id === recipient.relation_id &&
              student.entries.some((e) => isAssessmentEntryComment(e) && e.comment),
          );
        }),
      ),
    [assessments, recipient.relation_id],
  );

  const handleChangeSort = useCallback(
    (sortBy: GetAssessmentsEntriesSort['columnTextId']) => {
      const newSort: GetAssessmentsEntriesSort[] =
        sortBy === FilterKeys.EvaluationType
          ? [
              { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
              { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
              { columnTextId: 'group_type', direction: SORT_DIRECTION.DESC },
              { columnTextId: 'subject_order', direction: SORT_DIRECTION.ASC },
              { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
              { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
            ]
          : DEFAULT_REPORT_ASSESSMENTS_SORT;

      setParams((p) => ({ ...p, sort: newSort }));
    },
    [setParams],
  );

  const handleArrangeByChange = useCallback(
    (option: ReportAssessmentsArrangeBy) => {
      setArrangeBy(option);
      handleChangeSort(option === FilterKeys.Subject ? 'group_type' : 'evaluation_type');

      trackAmplitudeEvent({
        name: AnalyticsEventName.ReportChangeArrangementType,
        payload: {
          arrangement_type: option === FilterKeys.Subject ? 'subject' : 'area_of_learning',
        },
      });
    },
    [handleChangeSort],
  );

  const totalCount = data?.pages[0].count;

  return (
    <>
      <ReportsPreviewEntriesHeader>
        <Stack direction="row" alignItems="center">
          <Typography variant="h2">{getUserFullName(recipient)}</Typography>
          {!!totalCount && <Counter>{totalCount}</Counter>}
        </Stack>

        <Stack direction="row" gap={2} height={30} alignItems="center">
          <ArrangeByDropdown
            ref={(r: any) => (arrangeByDropdown.current = r as ArrangeByDropdown)}
            onSelectOption={handleArrangeByChange}
            selectedOption={arrangeBy}
            options={REPORT_ASSESSMENTS_ARRANGE_BY_FILTER_KEYS}
            renderLabel={() => (
              <>
                <Typography variant="h3" color="text.secondary">
                  {formatMessage({ id: 'filter-ArrangeBy' })}:
                </Typography>
                <Typography variant="h3" color="text.primary">
                  {formatMessage({ id: PROPERTIES_TEXT_IDS[arrangeBy] })}
                </Typography>
              </>
            )}
          />

          {hasComments && (
            <SimpleButton
              startIcon={<CommentFilledIcon />}
              inverse
              onClick={() => {
                const action = commentsShown ? hideComments : showComments;
                trackAmplitudeEvent({
                  name: AnalyticsEventName.ReportCommentsVisibilityToggle,
                  payload: { action: commentsShown ? 'hide_all' : 'show_all' },
                });
                return action();
              }}
            >
              <FormattedMessage
                id={commentsShown ? 'reports-HideAllComments' : 'reports-ShowAllComments'}
              />
            </SimpleButton>
          )}
        </Stack>
      </ReportsPreviewEntriesHeader>

      <ReportsPreviewEntriesContent ref={containerRef} sx={{ zIndex: 0 }}>
        {isLoading ? (
          <ReportPreviewEntriesListSkeleton rowCount={SKELETON_ROWS_COUNT} />
        ) : (
          <WithRef containerRef={containerRef}>
            {entries.map(([key, assessments]) => {
              return (
                <Box key={key}>
                  <ListViewAssessmentEntries
                    assessments={assessments}
                    lists={lists}
                    relationId={recipient.relation_id}
                    showAllComments={commentsShown}
                    wideComments
                    onAllCommentsClosed={hideComments}
                    onAllCommentsOpen={showComments}
                    fullView={fullView}
                    arrangeBy={arrangeBy}
                  />
                </Box>
              );
            })}
          </WithRef>
        )}
        {hasNextPage && (
          <>
            {isFetchingNextPage && (
              <ReportPreviewEntriesListSkeleton
                rowCount={Math.min(
                  PAGE_SIZE,
                  total && data ? total - data.pages.length * PAGE_SIZE : PAGE_SIZE,
                )}
              />
            )}
            <Box ref={loaderRef} />
          </>
        )}
      </ReportsPreviewEntriesContent>
    </>
  );
};

const ReportPreviewEntriesListSkeleton = ({ rowCount }: { rowCount: number }) => {
  return (
    <Box>
      {[...new Array(rowCount)].map((_, i) => {
        return (
          <GridRowStyled sx={{ pointerEvents: 'none', overflow: 'hidden' }} key={i}>
            <GridRowItem gap={1} sx={{ padding: 1.25 }}>
              <GridRowCell minWidth={60} variant={'h3'}>
                <Skeleton width={30} />
              </GridRowCell>
              <GridRowName minWidth={200} variant={'h3'}>
                <Skeleton width={`${randomInt(50, 100)}%`} />
              </GridRowName>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
            </GridRowItem>
          </GridRowStyled>
        );
      })}
    </Box>
  );
};
